import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Mail, ArrowLeft, AlertCircle, CheckCircle, 
  Key, Lock, Eye, EyeOff, RefreshCw 
} from 'lucide-react';
import useAuthStore from '../../store/authStore';

const otpSchema = yup.object({
  otp: yup.string()
    .length(6, 'OTP must be 6 digits')
    .matches(/^[0-9]+$/, 'OTP must contain only numbers')
    .required('OTP is required'),
});

const passwordSchema = yup.object({
  newPassword: yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

const ResetPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: OTP, 2: New Password
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [email, setEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const navigate = useNavigate();
  const { 
    forgotPassword, 
    verifyResetOtp, 
    resetPassword, 
    resendResetOtp,
    isLoading, 
    error 
  } = useAuthStore();

  const {
    register: registerOtp,
    handleSubmit: handleOtpSubmit,
    formState: { errors: otpErrors },
  } = useForm({
    resolver: yupResolver(otpSchema),
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
  } = useForm({
    resolver: yupResolver(passwordSchema),
  });

  // Get email from location state or prompt user
  React.useEffect(() => {
    const stateEmail = window.history.state?.usr?.email;
    if (stateEmail) {
      setEmail(stateEmail);
    } else {
      // If no email in state, redirect to forgot password
      navigate('/forgot-password');
    }
  }, [navigate]);

  const onOtpSubmit = async (data) => {
    try {
      const response = await verifyResetOtp(email, data.otp);
      setResetToken(response.data.resetToken);
      setStep(2);
    } catch (error) {
      console.error('OTP verification error:', error);
    }
  };

  const onPasswordSubmit = async (data) => {
    try {
      await resetPassword(resetToken, data.newPassword);
      // Show success and redirect to login
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      console.error('Password reset error:', error);
    }
  };

  const handleResendOtp = async () => {
    try {
      await resendResetOtp(email);
      setResendCooldown(60);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Resend OTP error:', error);
    }
  };

  if (step === 2) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
            <h2 className="text-xl font-bold text-gray-800">Set new password</h2>
          </div>
          <p className="text-sm text-gray-500">
            Enter your new password below.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 rounded-xl p-4 shadow-neomorph-inset border border-red-200/50">
            <div className="flex items-start">
              <div className="p-1.5 bg-red-100 rounded-md shadow-neomorph-inset mr-3">
                <AlertCircle className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h3 className="text-xs font-medium text-red-800">Error</h3>
                <p className="text-xs text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form className="space-y-5" onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
          <div>
            <label htmlFor="newPassword" className="block text-xs font-medium text-gray-600 mb-1.5">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...registerPassword('newPassword')}
                className={`block w-full pl-9 pr-10 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                  passwordErrors.newPassword ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                }`}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            {passwordErrors.newPassword && (
              <p className="mt-1.5 text-xs text-red-600">{passwordErrors.newPassword.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-600 mb-1.5">
              Confirm New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...registerPassword('confirmPassword')}
                className={`block w-full pl-9 pr-10 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                  passwordErrors.confirmPassword ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
                }`}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
            {passwordErrors.confirmPassword && (
              <p className="mt-1.5 text-xs text-red-600">{passwordErrors.confirmPassword.message}</p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-3 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Resetting...
                </div>
              ) : (
                'Reset Password'
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center mb-2">
          <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
          <h2 className="text-xl font-bold text-gray-800">Enter verification code</h2>
        </div>
        <p className="text-sm text-gray-500 ml-3">
          We've sent a 6-digit OTP to <span className="font-semibold text-primary-600">{email}</span>
        </p>
      </div>

      {error && (
        <div className="bg-red-50 rounded-xl p-4 shadow-neomorph-inset border border-red-200/50">
          <div className="flex items-start">
            <div className="p-1.5 bg-red-100 rounded-md shadow-neomorph-inset mr-3">
              <AlertCircle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h3 className="text-xs font-medium text-red-800">Error</h3>
              <p className="text-xs text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form className="space-y-5" onSubmit={handleOtpSubmit(onOtpSubmit)}>
        <div>
          <label htmlFor="otp" className="block text-xs font-medium text-gray-600 mb-1.5">
            6-digit OTP
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Key className="h-4 w-4 text-gray-400" />
            </div>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              {...registerOtp('otp')}
              className={`block w-full pl-9 pr-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                otpErrors.otp ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
              }`}
              placeholder="123456"
              maxLength={6}
            />
          </div>
          {otpErrors.otp && (
            <p className="mt-1.5 text-xs text-red-600">{otpErrors.otp.message}</p>
          )}
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-3 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verifying...
              </div>
            ) : (
              'Verify OTP'
            )}
          </button>
        </div>

        <div className="flex items-center justify-between">
          <Link
            to="/forgot-password"
            className="text-xs font-medium text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-1.5 shadow-neomorph-inset inline-flex items-center"
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Back
          </Link>
          
          <button
            type="button"
            onClick={handleResendOtp}
            disabled={resendCooldown > 0 || isLoading}
            className="text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-100 rounded-lg px-3 py-1.5 shadow-neomorph-inset inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${resendCooldown > 0 ? 'animate-spin' : ''}`} />
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordPage;